import React, { useState } from 'react';
import { functions2D } from '../../data/functions2d';
import { functions3D } from '../../data/functions3d';
import { MathItem } from '../../types/math';
import { MathFormula } from '../game/MathFormula';
import { Plot2D } from '../renderers/Plot2D';
import { Plot3D } from '../renderers/Plot3D';
import { Search, BookOpen, Layers, Box } from 'lucide-react';

interface EquationLibraryProps {
  onSelectForLab?: (item: MathItem) => void;
}

export const EquationLibrary: React.FC<EquationLibraryProps> = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | '2d' | '3d'>('all');

  const allItems = [...functions2D, ...functions3D];

  const filtered = allItems.filter((it) => {
    if (activeTab === '2d' && it.dimension !== '2d') return false;
    if (activeTab === '3d' && it.dimension !== '3d') return false;

    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      it.title.toLowerCase().includes(q) ||
      it.category.toLowerCase().includes(q) ||
      it.formulaLatex.toLowerCase().includes(q) ||
      it.intuition.summary.toLowerCase().includes(q)
    );
  });

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6">
      {/* Search & Filter bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-900/60 p-4 rounded-xl border border-slate-800">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-teal-400" />
          <h2 className="text-lg font-semibold text-white">Mathematical Curricula & Gallery</h2>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          {/* Search bar */}
          <div className="relative flex-1 sm:w-64">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search equations, curves..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 bg-slate-950 border border-slate-800 rounded-lg text-xs font-mono text-slate-200 placeholder-slate-500 focus:outline-none focus:border-teal-500"
            />
          </div>

          {/* Dimension tabs */}
          <div className="flex items-center gap-1 bg-slate-950 p-1 rounded-lg border border-slate-800">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-2.5 py-1 text-xs font-mono rounded ${
                activeTab === 'all' ? 'bg-slate-800 text-teal-300' : 'text-slate-400'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setActiveTab('2d')}
              className={`px-2.5 py-1 text-xs font-mono rounded flex items-center gap-1 ${
                activeTab === '2d' ? 'bg-slate-800 text-teal-300' : 'text-slate-400'
              }`}
            >
              <Layers className="w-3 h-3" /> 2D
            </button>
            <button
              onClick={() => setActiveTab('3d')}
              className={`px-2.5 py-1 text-xs font-mono rounded flex items-center gap-1 ${
                activeTab === '3d' ? 'bg-slate-800 text-purple-300' : 'text-slate-400'
              }`}
            >
              <Box className="w-3 h-3" /> 3D
            </button>
          </div>
        </div>
      </div>

      {/* Grid of cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((item) => (
          <div
            key={item.id}
            className="bg-slate-900/70 border border-slate-800/80 rounded-xl overflow-hidden hover:border-slate-700 transition flex flex-col shadow-lg"
          >
            {/* Viewport */}
            <div className="w-full h-48 relative border-b border-slate-800/60">
              {item.dimension === '2d' ? (
                <Plot2D item={item} showAsymptotes={true} interactive={false} />
              ) : (
                <Plot3D item={item} interactive={false} />
              )}
            </div>

            {/* Content */}
            <div className="p-4 flex-1 flex flex-col justify-between space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">
                    {item.dimension.toUpperCase()} · {item.category}
                  </span>
                  <span className="text-xs font-semibold text-slate-200">{item.title}</span>
                </div>

                <div className="text-center py-2 bg-slate-950/60 rounded-lg border border-slate-800/60 text-teal-300 text-sm">
                  <MathFormula latex={item.formulaLatex} displayMode={false} />
                </div>

                <p className="text-xs text-slate-400 mt-2 line-clamp-2">
                  {item.intuition.summary}
                </p>
              </div>

              <div className="border-t border-slate-800/60 pt-2 text-[11px] font-mono text-slate-500">
                {item.intuition.keySignatures[0]}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
